import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, BUTTON_THEME, CircularLoader } from '@st-operations/uikit/src/v2/index.js'
import toast from '@st-operations/uikit/lib/toast.js'

import './styles.scss'

import CollapsibleSection from '_components/collapsible-section/index.jsx'
import DeliveryStatus from '_components/delivery-status/index.jsx'
import DeliveryFileConfigurationForm from '_components/delivery-file-configuration-form/index.jsx'
import DeliveryDestinationConfigurationForm from '_components/delivery-destination-configuration-form/index.jsx'

import UUPDeliveryFrequencyForm from './uup-delivery-frequency-form/index.jsx'
import { isValidSecretsManagerArn } from '_lib/secretsManagerArn.js'

const DELIVERY_STATUS = {
  PROCESSED: 'processed',
  SUBMITTED: 'submitted',
  DRAFT: 'draft',
  ARCHIVED: 'archived',
}

/** UUP-specific S3 path presets (CDF / event-feeds uses a different list in its own form). */
const SHARETHIS_S3_PATH_OPTIONS = [
  {
    label: 's3://sharethis-daas-feeds/uup/<name> (Feeds)',
    value: 'sharethis-daas-feeds/uup',
  },
  {
    label: 's3://sharethis-sdf/uup/<name> (Social Data Feeds - Legacy)',
    value: 'sharethis-sdf/uup',
  },
]

/**
 * Backwards compatibility: maps a legacy delivery config (destination + s3AccessMethod)
 * to the new single `deliveryMethod` value. Returns undefined for combinations without a
 * direct equivalent (e.g. the removed IAM-role access methods) so the caller can fall back
 * to the default.
 */
const mapLegacyDeliveryMethod = ({ destination, s3AccessMethod } = {}) => {
  if (destination === 'sharethis-s3') return 's3-internal'
  if (destination === 'partner-s3') {
    if (s3AccessMethod === 's3-access-key') return 's3-external-access-keys'
    if (s3AccessMethod === 'secrets-manager-arn') return 's3-external-secrets-manager-arn'
  }
  if (destination === 'gcs') {
    if (s3AccessMethod === 'gcs-credentials') return 'gcs-credentials'
    if (s3AccessMethod === 'secrets-manager-arn') return 'gcs-secrets-manager-arn'
  }
  return undefined
}

/**
 * Handles the configuration and management of data delivery settings
 * including delivery frequency, file configuration, and destination configuration.
 *
 * The component provides functionality to:
 * - Configure delivery frequency and scheduling
 * - Set file format and compression settings
 * - Configure destination details (GCS, partner S3, ShareThis S3 per UUP contract)
 * - Save configurations as draft or submit them
 * - Archive existing delivery configurations
 *
 * @component
 * @param {Object} props
 * @param {Object} props.schema - Schema definition for the delivery configuration
 * @param {Object} props.data - Initial delivery configuration data
 * @param {boolean} props.loadingSave - Flag indicating if save operation is in progress
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {Function} props.onSave - Callback function to handle save operations
 */
const DeliveryContent = ({ schema = {}, data: initialData = {}, loadingSave, onClose, onSave }) => {
  const [data, setData] = useState({})
  const [requiredFields, setRequiredFields] = useState({})

  const checkRequiredFields = () => {
    const deliveryMethod = data.deliveryMethod
    const isExternalAccessKeys = deliveryMethod === 's3-external-access-keys'
    const isSecretsManager =
      deliveryMethod === 's3-external-secrets-manager-arn' ||
      deliveryMethod === 'gcs-secrets-manager-arn'
    const isGcsCredentials = deliveryMethod === 'gcs-credentials'
    const credsSecretArnValue = data.credsSecretArn?.trim() || ''

    const requiredFieldsMap = {
      name: !data.name?.trim().length > 0,

      //file configuration
      compression: !data.compression,
      format: !data.format,
      numFiles: !data.numFiles,

      //delivery frequency
      frequency: !data.frequency,
      mode: !data.mode && data.isSample === true,
      startDate: !data.startDate && data.isSample === true,
      endDate: !data.endDate && data.isSample === true,

      // UUP delivery: single deliveryMethod selector drives the required credential fields.
      deliveryName: !data.deliveryName?.trim().length > 0,
      deliveryMethod: !deliveryMethod,
      // ShareThis S3 copy path — required for every delivery method.
      s3Path: !(data.s3Path && String(data.s3Path).trim()),
      remoteS3Path: isExternalAccessKeys && !data.remoteS3Path?.trim().length > 0,
      s3AccessKeyId: isExternalAccessKeys && !data.s3AccessKeyId?.trim().length > 0,
      s3SecretAccessKey: isExternalAccessKeys && !data.s3SecretAccessKey?.trim().length > 0,
      credsSecretArn: isSecretsManager && !credsSecretArnValue.length > 0,
      credsSecretArnInvalid:
        isSecretsManager &&
        credsSecretArnValue.length > 0 &&
        !isValidSecretsManagerArn(credsSecretArnValue),
      gcsProject: isGcsCredentials && !data.gcsProject,
      gcsRemotePath: isGcsCredentials && !data.gcsRemotePath,
      gcsServiceAccount: isGcsCredentials && !data.gcsServiceAccount,
    }
    setRequiredFields(requiredFieldsMap)

    const result = Object.values(requiredFieldsMap).some((value) => value)
    if (result) {
      toast.error('There are some required field that must be filled')
    }

    return result
  }

  useEffect(
    function refreshData() {
      // Respect an explicit deliveryMethod, else derive it from the legacy
      // destination + s3AccessMethod shape, else default to "s3-internal".
      const deliveryMethod =
        initialData.deliveryMethod || mapLegacyDeliveryMethod(initialData) || 's3-internal'
      setData({ ...initialData, deliveryMethod })
    },
    [initialData]
  )

  const resetRequiredFields = () => {
    setRequiredFields({})
  }

  const handleSaveAsDraft = () => {
    resetRequiredFields()
    onSave({ ...data })?.then(() => onClose())
  }

  const handleSubmit = () => {
    if (checkRequiredFields()) {
      return
    }
    onSave({ ...data, status: DELIVERY_STATUS.SUBMITTED })?.then(() => onClose())
  }

  const handleArchive = () => {
    onSave({ ...data, status: DELIVERY_STATUS.ARCHIVED })?.then(() => onClose())
  }

  return (
    <section className="delivery-modal">
      <DeliveryStatus className="delivery-content__status" data={data} />
      <div className="delivery-content__content">
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">Delivery Frequency</h3>}
            description="How often data is delivered"
          >
            <UUPDeliveryFrequencyForm
              schema={schema}
              data={data}
              onDataChange={setData}
              requiredErrorFrequency={requiredFields.frequency}
              requiredErrorMode={requiredFields.mode}
              requiredErrorFrequencyStartDate={requiredFields.startDate}
              requiredErrorFrequencyEndDate={requiredFields.endDate}
            />
          </CollapsibleSection>
        </div>
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">File Configuration</h3>}
          >
            <DeliveryFileConfigurationForm
              schema={schema}
              data={data}
              onDataChange={setData}
              requiredErrorFileFormat={requiredFields.format}
              requiredErrorFileCompression={requiredFields.compression}
              requiredErrorNumFiles={requiredFields.numFiles}
            />
          </CollapsibleSection>
        </div>
        <div className="delivery-content__content-section">
          <CollapsibleSection
            title={<h3 className="delivery-content__content-title">Destination Configuration</h3>}
          >
            <DeliveryDestinationConfigurationForm
              data={data}
              onDataChange={setData}
              sharethisS3PathOptions={SHARETHIS_S3_PATH_OPTIONS}
              requiredErrorDeliveryMethod={requiredFields.deliveryMethod}
              requiredErrorDestinationName={requiredFields.deliveryName}
              requiredErrorBucket={requiredFields.s3Path}
              requiredErrorRemoteS3Path={requiredFields.remoteS3Path}
              requiredErrorS3AccessKeyId={requiredFields.s3AccessKeyId}
              requiredErrorS3SecretAccessKeyId={requiredFields.s3SecretAccessKey}
              requiredErrorCredsSecretArn={requiredFields.credsSecretArn}
              invalidErrorCredsSecretArn={requiredFields.credsSecretArnInvalid}
              requiredErrorProject={requiredFields.gcsProject}
              requiredErrorRemotePath={requiredFields.gcsRemotePath}
              requiredErrorServiceAccount={requiredFields.gcsServiceAccount}
            />
          </CollapsibleSection>
        </div>
      </div>
      <footer className="delivery-content__footer">
        <Choose>
          <When condition={loadingSave}>
            <CircularLoader className="delivery-content__loading" />
          </When>
          <When condition={data?.status === DELIVERY_STATUS.DRAFT}>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              className="delivery-content__footer-button"
              onClick={handleSaveAsDraft}
            >
              Save as Draft
            </Button>
            <Button className="delivery-content__footer-button" onClick={handleSubmit}>
              Submit
            </Button>
          </When>
          <When condition={data?.status === DELIVERY_STATUS.ARCHIVED}>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              className="delivery-content__footer-button"
              onClick={handleSaveAsDraft}
            >
              Save Changes
            </Button>
            <Button className="delivery-content__footer-button" onClick={handleSubmit}>
              Submit
            </Button>
          </When>
          <Otherwise>
            <Button
              theme={BUTTON_THEME.SECONDARY}
              className="delivery-content__footer-button"
              onClick={handleSaveAsDraft}
            >
              Save Changes
            </Button>
            <Button className="delivery-content__footer-button" onClick={handleArchive}>
              Archive
            </Button>
          </Otherwise>
        </Choose>
      </footer>
    </section>
  )
}

DeliveryContent.propTypes = {
  schema: PropTypes.object,
  data: PropTypes.object,
  loadingSave: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default React.memo(DeliveryContent)
